import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { IconBarcode } from '@tabler/icons';
import api from '../../../../services/api';
import SgqNc, { fetchSgqIndicators, useSgqEnabled } from '../../../../../ui-component/sgq/SgqNc';
import PageHeader from '../../../../../ui-component/extended/PageHeader';

const formatDateBR = (value) => {
    if (!value) return '-';
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
};

const SERIAL_STATUS_LABEL = {
    available: { label: 'Disponível', color: 'success' },
    reserved: { label: 'Reservado', color: 'warning' },
    sold: { label: 'Vendido/Baixado', color: 'default' }
};

const diasParaVencer = (dataValidade) => {
    if (!dataValidade) return null;
    const hoje = new Date().toISOString().slice(0, 10);
    const diff = (new Date(dataValidade) - new Date(hoje)) / (1000 * 60 * 60 * 24);
    return Math.round(diff);
};

const LotsAndSerials = () => {
    const [appId, setAppId] = useState(null);
    const [tab, setTab] = useState('lot');
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseFilter, setWarehouseFilter] = useState('');
    const [lots, setLots] = useState([]);
    const [serials, setSerials] = useState([]);
    const [serialStatusFilter, setSerialStatusFilter] = useState('available');
    const sgqEnabled = useSgqEnabled();
    const [sgqIndicators, setSgqIndicators] = useState({});

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            const res = await api.get(`v1/apps/${appId}/inventory/warehouses/`);
            setWarehouses(res.data || []);
        })();
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            const params = new URLSearchParams();
            if (warehouseFilter) params.set('warehouse', warehouseFilter);
            if (tab === 'lot') {
                const res = await api.get(`v1/apps/${appId}/inventory/lots/?${params.toString()}`);
                setLots(res.data.lots || []);
            } else {
                if (serialStatusFilter) params.set('status', serialStatusFilter);
                const res = await api.get(`v1/apps/${appId}/inventory/serial-numbers/?${params.toString()}`);
                setSerials(res.data.serial_numbers || []);
            }
        })();
    }, [appId, tab, warehouseFilter, serialStatusFilter]);

    // Indicadores de NC por lote (uma chamada para a tabela inteira). Sem SGQ ligado, nada é buscado.
    useEffect(() => {
        if (!appId || !sgqEnabled || tab !== 'lot' || lots.length === 0) return;
        fetchSgqIndicators(
            appId,
            'lot',
            lots.map((l) => l.id)
        )
            .then(setSgqIndicators)
            .catch(() => setSgqIndicators({}));
    }, [appId, sgqEnabled, tab, lots]);

    return (
        <Box>
            <PageHeader
                icon={IconBarcode}
                gradient="blue"
                title="Lotes e Números de Série"
                subtitle="Rastreabilidade por lote (validade/FEFO) e por unidade serializada"
                actions={
                    <Stack direction="row" spacing={2}>
                        <ToggleButtonGroup size="small" value={tab} exclusive onChange={(e, v) => v && setTab(v)}>
                            <ToggleButton value="lot">Lotes</ToggleButton>
                            <ToggleButton value="serial">Números de Série</ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                            select
                            size="small"
                            label="Depósito"
                            value={warehouseFilter}
                            onChange={(e) => setWarehouseFilter(e.target.value)}
                            sx={{ width: 180 }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {warehouses.map((w) => (
                                <MenuItem key={w.id} value={w.id}>
                                    {w.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        {tab === 'serial' && (
                            <TextField
                                select
                                size="small"
                                label="Status"
                                value={serialStatusFilter}
                                onChange={(e) => setSerialStatusFilter(e.target.value)}
                                sx={{ width: 180 }}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="available">Disponível</MenuItem>
                                <MenuItem value="reserved">Reservado</MenuItem>
                                <MenuItem value="sold">Vendido/Baixado</MenuItem>
                            </TextField>
                        )}
                    </Stack>
                }
            />

            <Card>
                <CardContent>
                    {tab === 'lot' ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Produto</TableCell>
                                    <TableCell>Depósito</TableCell>
                                    <TableCell>Lote</TableCell>
                                    <TableCell>Validade</TableCell>
                                    <TableCell align="right">Saldo</TableCell>
                                    {sgqEnabled && <TableCell>Qualidade</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lots.map((lot) => {
                                    const dias = diasParaVencer(lot.data_validade);
                                    return (
                                        <TableRow key={lot.id}>
                                            <TableCell>{lot.produto_variacao_nome}</TableCell>
                                            <TableCell>{lot.warehouse_name}</TableCell>
                                            <TableCell>{lot.numero_lote}</TableCell>
                                            <TableCell>
                                                {formatDateBR(lot.data_validade)}
                                                {dias !== null && dias <= 15 && (
                                                    <Chip
                                                        label={dias < 0 ? 'Vencido' : `Vence em ${dias}d`}
                                                        color={dias < 0 ? 'error' : 'warning'}
                                                        size="small"
                                                        sx={{ ml: 1 }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="right">{lot.quantidade}</TableCell>
                                            {sgqEnabled && (
                                                <TableCell>
                                                    <SgqNc
                                                        appId={appId}
                                                        entityType="lot"
                                                        entityId={lot.id}
                                                        indicator={sgqIndicators[lot.id] || { open: 0, items: [] }}
                                                        defaultTitle={`Problema no lote ${lot.numero_lote}`}
                                                        onOpened={() => setLots((prev) => [...prev])}
                                                    />
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                                {lots.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={sgqEnabled ? 6 : 5} align="center">
                                            Nenhum lote encontrado
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Produto</TableCell>
                                    <TableCell>Depósito</TableCell>
                                    <TableCell>Número de Série</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {serials.map((serial) => {
                                    const statusInfo = SERIAL_STATUS_LABEL[serial.status] || { label: serial.status, color: 'default' };
                                    return (
                                        <TableRow key={serial.id}>
                                            <TableCell>{serial.produto_variacao_nome}</TableCell>
                                            <TableCell>{serial.warehouse_name || '-'}</TableCell>
                                            <TableCell>{serial.numero_serie}</TableCell>
                                            <TableCell>
                                                <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {serials.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            Nenhum número de série encontrado
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default LotsAndSerials;
